'use client';

import React, { useEffect, useRef } from 'react';

const freeHoursItems = [
  {
    hours: '2',
    label: 'Hours',
    title: 'End-to-End Demo of SAC and SAC Planning',
    desc: 'A structured walkthrough of SAP Analytics Cloud and SAC Planning — covering the full product landscape, planning model anatomy, data actions, and planning stories. Designed to give your team a clear picture of what the platform can and cannot do before any commitment is made.',
  },
  {
    hours: '2',
    label: 'Hours',
    title: 'SAP Activate Methodology for SAC Planning Implementation',
    desc: 'An overview of how SAP Activate applies specifically to SAC Planning programs — covering the Prepare, Explore, Realise, and Deploy phases. Includes how to structure workstreams, manage scope, and avoid the most common implementation failure points.',
  },
  {
    hours: '2',
    label: 'Hours',
    title: 'Discover: Enterprise Needs Mapping',
    desc: 'A facilitated session to map your organisation\'s planning requirements against SAC Planning capabilities. Covers planning scope, data sources, integration points, user roles, and governance needs — producing a structured discovery output that informs architecture decisions.',
  },
  {
    hours: '4',
    label: 'Hours',
    title: 'One Use Case Development',
    desc: 'Hands-on development of a single planning use case — from dimension design through data model build to a working planning story. Demonstrates real implementation quality and gives your team a tangible reference point for the full engagement.',
  },
];

export default function FreeHoursSection() {
  const sectionRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const section = sectionRef.current;
    if (!section) return;
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.querySelectorAll('.reveal-on-scroll').forEach((el) => {
              (el as HTMLElement).classList.add('is-visible');
            });
          }
        });
      },
      { threshold: 0.02 }
    );
    observer.observe(section);
    return () => observer.disconnect();
  }, []);

  return (
    <section ref={sectionRef} className="py-20 px-6 border-b border-border bg-secondary">
      <div className="max-w-container mx-auto">
        <div className="mono-label mb-4 reveal-on-scroll">Free Engagement</div>
        <div className="mb-12 reveal-on-scroll">
          <h2
            className="font-heading font-semibold text-foreground leading-tight mb-3"
            style={{ fontSize: 'clamp(1.5rem, 3vw, 2.25rem)', letterSpacing: '-0.025em' }}
          >
            What Is Included in Your Free 10 Hours
          </h2>
          <p className="text-sm text-muted font-body max-w-2xl leading-relaxed">
            The free engagement is structured — not exploratory. Each block has a defined output.
            Ten hours is sufficient to assess fit, establish architecture direction, and demonstrate
            delivery quality before any commercial commitment.
          </p>
        </div>

        <div className="flex flex-col gap-0">
          {freeHoursItems.map((item, i) => (
            <div
              key={i}
              className={`reveal-on-scroll reveal-delay-${Math.min(i + 1, 4)} border border-border mb-3 bg-white`}
              style={{ borderRadius: '4px' }}
            >
              <div className="grid grid-cols-1 md:grid-cols-12">
                {/* Hour block */}
                <div className="md:col-span-2 flex flex-col items-center justify-center py-8 px-6 border-b md:border-b-0 md:border-r border-border bg-foreground">
                  <span
                    className="font-heading font-semibold text-white leading-none"
                    style={{ fontSize: 'clamp(2rem, 4vw, 3rem)', letterSpacing: '-0.04em' }}
                  >
                    {item.hours}
                  </span>
                  <span className="mono-label text-white opacity-60 mt-1" style={{ fontSize: '0.6rem' }}>
                    {item.label}
                  </span>
                </div>
                {/* Content block */}
                <div className="md:col-span-10 p-8">
                  <h3 className="font-heading font-semibold text-foreground text-base mb-3 leading-snug">
                    {item.title}
                  </h3>
                  <p className="text-sm text-muted leading-relaxed font-body">{item.desc}</p>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="mt-10 pt-8 border-t border-border reveal-on-scroll">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <div className="mono-label mb-1">Total</div>
              <p className="font-heading font-semibold text-foreground text-lg" style={{ letterSpacing: '-0.02em' }}>
                10 Hours — No Obligation
              </p>
              <p className="text-sm text-muted font-body mt-1">
                No contract required. No vendor pitch. Structured outputs at every stage.
              </p>
            </div>
            <a
              href="mailto:free10hours@sacplans.com"
              className="btn-primary text-sm shrink-0"
            >
              Claim Your Free 10 Hours
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}
